import React from 'react';
import { infoPageStyles as styles } from './sharedStyles';

const TermsOfService = () => {
  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Terms of Service</h1>
        <p style={styles.headerSubtitle}>Last updated: November 5, 2025</p>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.section}>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>Important Notice:</strong> RedBird Markets is an independent student project and is NOT affiliated with, 
              endorsed by, or sponsored by Illinois State University. The terms "ISU," "Redbird," and "Illinois State University" 
              are used only descriptively to indicate the target user community. The use of the word "Redbird" is purely descriptive. 
              If Illinois State University requests, we will rebrand accordingly. All transactions occur directly between users. 
              RedBird Markets is not a party to any sale and accepts no responsibility for item condition, delivery, payment issues, 
              fraud, or user conduct.
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Acceptance of Terms</h2>
          <p style={styles.text}>
            By accessing and using RedBird Markets ("Platform," "Service," "we," "us"), you ("User," "you") accept and 
            agree to be bound by these Terms of Service ("Terms"). If you do not agree to these terms, you must not use 
            our platform. Your continued use after any modifications constitutes acceptance of the updated terms.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Eligibility</h2>
          <p style={styles.text}>
            RedBird Markets is exclusively available to current Illinois State University students, faculty, 
            and staff with a valid ISU email address. Users must be at least 18 years old.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Must have an active ISU email address</li>
            <li style={styles.listItem}>Must verify email address before using platform features</li>
            <li style={styles.listItem}>Must be at least 18 years of age</li>
            <li style={styles.listItem}>Must maintain current ISU affiliation</li>
            <li style={styles.listItem}>Must comply with all ISU policies and regulations</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. User Accounts</h2>
          <p style={styles.text}>
            You are responsible for maintaining the confidentiality of your account credentials and for all 
            activities that occur under your account. We may suspend or terminate accounts for policy violations 
            without prior notice.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide accurate and complete information</li>
            <li style={styles.listItem}>Keep your password secure and confidential</li>
            <li style={styles.listItem}>Notify us immediately of any unauthorized access</li>
            <li style={styles.listItem}>One account per person - multiple accounts are prohibited</li>
            <li style={styles.listItem}>We reserve the right to terminate any account without prior notice for violations</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Prohibited Activities</h2>
          <p style={styles.text}>Users may not:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>List illegal, stolen, counterfeit, or prohibited items</li>
            <li style={styles.listItem}>List event tickets not permitted by university policy</li>
            <li style={styles.listItem}>Sell digital downloads or intangible goods not approved by the platform</li>
            <li style={styles.listItem}>Engage in fraudulent, deceptive, or misleading practices</li>
            <li style={styles.listItem}>Harass, threaten, abuse, or discriminate against other users</li>
            <li style={styles.listItem}>Violate any local, state, federal, or international laws</li>
            <li style={styles.listItem}>Circumvent platform fees or payment processing</li>
            <li style={styles.listItem}>Accept payments outside of Stripe (cash, Venmo, Zelle, etc.) - such transactions are at your own risk</li>
            <li style={styles.listItem}>Create fake accounts, impersonate others, or misrepresent identity</li>
            <li style={styles.listItem}>Scrape, harvest, or collect user data without permission</li>
            <li style={styles.listItem}>Post spam, irrelevant content, or unsolicited advertisements</li>
            <li style={styles.listItem}>Interfere with platform operations or security features</li>
          </ul>
          <p style={styles.text}>
            <strong>Note:</strong> Multiple violations may result in permanent account termination and may be reported 
            to ISU Administration or law enforcement if necessary.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Listings and Content</h2>
          <p style={styles.text}>
            Users are solely responsible for all content they post on RedBird Markets. You retain ownership of your 
            listings but grant us a non-exclusive, worldwide, royalty-free license to display, reproduce, and distribute 
            your content on the platform.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Listings must accurately and completely describe items, including all flaws</li>
            <li style={styles.listItem}>Photos must be of the actual item being sold (no stock photos)</li>
            <li style={styles.listItem}>Prices must be clearly stated in U.S. dollars</li>
            <li style={styles.listItem}>Content must not violate copyrights, trademarks, or intellectual property rights</li>
            <li style={styles.listItem}>We reserve the right to remove any content without notice or explanation</li>
            <li style={styles.listItem}>Removed content does not entitle users to refunds or compensation</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5.5. Listing Management and Archive System</h2>
          <p style={styles.text}>
            Users have full control over their listings through our management system:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Active Listings:</strong> Visible in marketplace until sold or archived</li>
            <li style={styles.listItem}><strong>Sold Status:</strong> Listings automatically marked as sold after successful payment</li>
            <li style={styles.listItem}><strong>Archive Option:</strong> Users may archive listings to hide them from marketplace while preserving transaction history</li>
            <li style={styles.listItem}><strong>Automatic Cleanup:</strong> Archived listings older than 90 days are moved to deep storage but never deleted</li>
            <li style={styles.listItem}><strong>Transaction Integrity:</strong> All listing data is permanently preserved to maintain payment records and buyer-seller communication history</li>
            <li style={styles.listItem}><strong>Contact Preservation:</strong> Buyers and sellers retain ability to contact each other for completed transactions regardless of archive status</li>
          </ul>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>Important:</strong> Listings involved in completed transactions are never permanently deleted, even when archived. 
              This ensures transaction records, payment history, and buyer-seller communication remain accessible for legal and 
              operational purposes.
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Transactions, Payments, and Stripe Processing</h2>
          <div style={styles.highlight}>
            <h3 style={styles.subsectionTitle}>No Platform Liability - Not a Party to Sales</h3>
            <p style={styles.text}>
              <strong>IMPORTANT:</strong> RedBird Markets is a marketplace platform only. We are NOT a party to any transaction 
              between buyers and sellers. All sales occur directly between users. We accept NO responsibility for:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Item condition, quality, safety, or legality</li>
              <li style={styles.listItem}>Delivery, pickup, or exchange arrangements</li>
              <li style={styles.listItem}>Fraud, misrepresentation, or user misconduct</li>
              <li style={styles.listItem}>Payment disputes, chargebacks, or refund requests</li>
              <li style={styles.listItem}>Accuracy of listings or user communications</li>
              <li style={styles.listItem}>Loss, damage, or theft of items</li>
            </ul>
          </div>

          <h3 style={styles.subsectionTitle}>Stripe Payment Processing</h3>
          <p style={styles.text}>
            All payments are processed exclusively through Stripe, Inc., a third-party payment processor. RedBird Markets:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Does NOT store credit card numbers</strong> - All payment data is handled by Stripe</li>
            <li style={styles.listItem}><strong>Never touches payment data</strong> - Stripe manages all PCI-compliant storage</li>
            <li style={styles.listItem}><strong>Does NOT process refunds</strong> - All refunds are handled by Stripe and card networks</li>
            <li style={styles.listItem}><strong>Seller Fees:</strong> 5% platform fee + Stripe processing fees (2.9% + $0.30) are automatically deducted from seller payouts</li>
            <li style={styles.listItem}>Payment processing is subject to Stripe's Terms of Service</li>
          </ul>

          <h3 style={styles.subsectionTitle}>ALL SALES FINAL</h3>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>ALL SALES ARE FINAL.</strong> Once a payment is completed through Stripe, there are NO refunds, reversals, 
              or credits issued by RedBird Markets under any circumstances. Buyers must inspect items thoroughly before purchasing. 
              Although RedBird Markets does not offer refunds, chargebacks may still be available under card network policies. 
              Outcomes of chargeback disputes are outside our control. Any payment disputes must be filed directly with Stripe 
              and your card issuer according to their chargeback policies.
            </p>
          </div>

          <h3 style={styles.subsectionTitle}>Off-Platform Payments</h3>
          <p style={styles.text}>
            <strong>WARNING:</strong> Any payments made outside of Stripe (cash, Venmo, Zelle, PayPal, etc.) are completely at your 
            own risk. RedBird Markets provides NO protection, dispute resolution, or support for off-platform transactions. Users 
            conducting off-platform payments violate these Terms and do so without any platform protections.
          </p>

          <h3 style={styles.subsectionTitle}>Tax Obligations</h3>
          <p style={styles.text}>
            Users are solely responsible for reporting income and paying all applicable taxes on transactions. Stripe, not RedBird 
            Markets, is responsible for tax reporting (e.g., 1099-K forms). Users remain responsible for their own tax filings. 
            RedBird Markets does not provide tax advice or reporting.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. User Safety and In-Person Exchanges</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>USERS ARE SOLELY RESPONSIBLE FOR THEIR OWN SAFETY.</strong> RedBird Markets does not verify user identities, 
              conduct background checks, or supervise in-person exchanges. We provide NO guarantee regarding user safety, trustworthiness, 
              or conduct.
            </p>
          </div>
          <p style={styles.text}>Safety Guidelines (User Responsibility):</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Meet ONLY in public, well-lit areas on ISU campus (Bone Student Center, Milner Library, etc.)</li>
            <li style={styles.listItem}>NEVER meet at private residences, dorms, or isolated locations</li>
            <li style={styles.listItem}>Bring a friend or notify someone of meeting details</li>
            <li style={styles.listItem}>Inspect items thoroughly before payment/exchange</li>
            <li style={styles.listItem}>Trust your instincts - if something feels wrong, cancel the meeting</li>
            <li style={styles.listItem}>Report suspicious behavior to ISU Police (438-8631) if necessary</li>
          </ul>
          <p style={styles.text}>
            <strong>Platform Communication:</strong> RedBird Markets is only responsible for messages sent through our platform. 
            We are NOT responsible for any communications conducted off-platform (text messages, phone calls, social media DMs, emails, etc.). 
            Users communicate off-platform at their own risk.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Disputes and Refunds</h2>
          <p style={styles.text}>
            <strong>RedBird Markets does NOT mediate disputes.</strong> All payment disputes, chargebacks, and refund requests are 
            handled exclusively by Stripe, Inc. and the card networks (Visa, Mastercard, American Express).
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>To dispute a payment, contact your card issuer (bank) within 60-120 days</li>
            <li style={styles.listItem}>Your bank will file a chargeback with Stripe on your behalf</li>
            <li style={styles.listItem}>Stripe handles all dispute processing according to card network rules</li>
            <li style={styles.listItem}>Card network decisions are final - neither RedBird Markets nor users can appeal</li>
            <li style={styles.listItem}>Sellers may be charged a $15 dispute fee by Stripe regardless of outcome</li>
            <li style={styles.listItem}><strong>Important for Sellers:</strong> Stripe may deduct disputed amounts and associated fees directly from payouts or balances during the dispute process</li>
          </ul>
          <p style={styles.text}>
            For complete dispute procedures, see our <a href="/dispute-resolution" style={styles.link}>Dispute Resolution</a> page.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Intellectual Property</h2>
          <p style={styles.text}>
            RedBird Markets owns all rights to the platform code, design, logo, branding, and original content. Users retain ownership 
            of their listing content but grant us a non-exclusive, worldwide, royalty-free license to display, reproduce, modify, 
            and distribute such content for platform operations.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Platform name, logo, and design are proprietary to RedBird Markets</li>
            <li style={styles.listItem}>Users may not copy, modify, or redistribute platform code or design</li>
            <li style={styles.listItem}>User-generated content license is perpetual and survives account deletion</li>
            <li style={styles.listItem}>Users must not infringe on third-party intellectual property rights</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. User Indemnification</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>You agree to indemnify, defend, and hold harmless RedBird Markets, its operators, affiliates, and service providers 
              from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</strong>
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Your use of the platform or services</li>
              <li style={styles.listItem}>Your violation of these Terms of Service</li>
              <li style={styles.listItem}>Your listings, transactions, or interactions with other users</li>
              <li style={styles.listItem}>Your infringement of any intellectual property or other rights</li>
              <li style={styles.listItem}>Any injuries, damages, or losses resulting from in-person exchanges</li>
              <li style={styles.listItem}>Any third-party claims related to your conduct on the platform</li>
            </ul>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Disclaimers and "As Is" Service</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>RedBird Markets is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied, 
              including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.</strong>
            </p>
          </div>
          <p style={styles.text}>We specifically disclaim all warranties regarding:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Quality, safety, legality, or condition of items listed</li>
            <li style={styles.listItem}>Truth, accuracy, or completeness of user listings and communications</li>
            <li style={styles.listItem}>Ability of users to complete transactions</li>
            <li style={styles.listItem}>Continuous, uninterrupted, secure, or error-free operation</li>
            <li style={styles.listItem}>User conduct, trustworthiness, or safety</li>
            <li style={styles.listItem}>Results or outcomes from platform use</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Limitation of Liability</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong> RedBird Markets, its operators, affiliates, and service 
              providers SHALL NOT BE LIABLE for any indirect, incidental, special, consequential, punitive, or exemplary damages, 
              including but not limited to:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Loss of profits, revenue, data, or business opportunities</li>
              <li style={styles.listItem}>Personal injury or property damage from in-person exchanges</li>
              <li style={styles.listItem}>Fraudulent transactions or user misconduct</li>
              <li style={styles.listItem}>Item defects, damage, or failure to meet expectations</li>
              <li style={styles.listItem}>Theft, loss, or destruction of items</li>
              <li style={styles.listItem}>Payment disputes, chargebacks, or failed transactions</li>
              <li style={styles.listItem}>Platform downtime, errors, or data loss</li>
            </ul>
            <p style={styles.text}>
              <strong>OUR MAXIMUM LIABILITY to you for any claim is limited to the amount of platform fees you paid in the 
              12 months preceding the claim, or $100 USD, whichever is less.</strong>
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>13. University Disclaimer</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>NOT AFFILIATED WITH ILLINOIS STATE UNIVERSITY:</strong> RedBird Markets is an independent student project and is 
              NOT affiliated with, endorsed by, sponsored by, or approved by Illinois State University, its Board of Trustees, administration, 
              faculty, or staff. The use of "ISU," "Redbird," "Illinois State University," and related terms is purely descriptive to indicate 
              the target user community (ISU-affiliated individuals). Illinois State University bears no responsibility for this platform, 
              its operations, transactions, or user conduct.
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Account Termination</h2>
          <p style={styles.text}>
            We reserve the right to suspend, restrict, or permanently terminate any user account at any time for any reason, 
            including but not limited to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Violation of these Terms of Service or Community Guidelines</li>
            <li style={styles.listItem}>Suspicious, fraudulent, or abusive activity</li>
            <li style={styles.listItem}>Multiple user complaints</li>
            <li style={styles.listItem}>Inactive accounts or loss of ISU affiliation</li>
            <li style={styles.listItem}>At our sole discretion without explanation</li>
          </ul>
          <p style={styles.text}>
            We will attempt to notify users of terminations where practical, but reserve the right to act immediately in cases of 
            fraud, safety risks, or legal violations. Account termination does not entitle users to refunds of fees paid.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>15. Governing Law and Jurisdiction</h2>
          <p style={styles.text}>
            These Terms of Service are governed by the laws of the State of Illinois, United States, without regard to conflict of law 
            principles. Any legal action or proceeding arising from these Terms or your use of the platform must be brought exclusively 
            in the state or federal courts located in McLean County, Illinois. You consent to the personal jurisdiction of these courts.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>16. Changes to Terms</h2>
          <p style={styles.text}>
            We may modify these Terms of Service at any time. Material changes will be posted on this page with an updated "Last Updated" 
            date. We may also notify users via email or platform notifications. Your continued use of the platform after changes constitutes 
            acceptance of the updated Terms. We maintain date-stamped records of all Terms versions in our version control system.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>17. Severability</h2>
          <p style={styles.text}>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue 
            in full force and effect. Invalid provisions will be interpreted to achieve their intended purpose to the maximum extent possible.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>18. Contact Information</h2>
          <p style={styles.text}>
            For questions about these Terms of Service, please contact us:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Through our <a href="/contact-us" style={styles.link}>Contact Us</a> page</li>
            <li style={styles.listItem}>Via our <a href="/help-center" style={styles.link}>Help Center</a></li>
          </ul>
          <p style={styles.text}>
            For payment disputes or refund requests, contact Stripe directly or your card issuer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
